import BinanceService from './binance_service.js';
import AliceBlueService from './aliceblue_service.js';
import FyersService from './fyers_service.js';
import UpstoxService from './upstox_service.js';
import DhanService from './dhan_service.js';
import CoinbaseService from './coinbase_service.js';
import KrakenService from './kraken_service.js';
import BybitService from './bybit_service.js';
import OkxService from './okx_service.js';
import AmfiService from './amfi_service.js';

// These five are free, public, no-key data sources — unlike the brokers
// below them they need no auth/session, so isAuthenticated() always treats
// them as ready and switchProvider() never routes them through an auth modal.
const FREE_PUBLIC_PROVIDERS = ['coinbase', 'kraken', 'bybit', 'okx', 'amfi'];

export default {
    currentProvider: null,
    providers: {
        binance: BinanceService,
        aliceblue: AliceBlueService,
        fyers: FyersService,
        upstox: UpstoxService,
        dhan: DhanService,
        coinbase: CoinbaseService,
        kraken: KrakenService,
        bybit: BybitService,
        okx: OkxService,
        amfi: AmfiService
    },

    // Initialize the data provider manager
    init() {
        // Try to load saved sessions in priority order
        if (UpstoxService.loadSavedSession()) {
            this.currentProvider = 'upstox';
        } else if (FyersService.loadSavedSession()) {
            this.currentProvider = 'fyers';
        } else if (DhanService.loadSavedSession()) {
            this.currentProvider = 'dhan';
        } else if (AliceBlueService.loadSavedSession()) {
            this.currentProvider = 'aliceblue';
        } else {
            this.currentProvider = 'binance'; // Default to Binance
        }
    },

    // Switch between data providers
    switchProvider(providerName) {
        if (!this.providers[providerName]) {
            throw new Error(`Unknown provider: ${providerName}`);
        }
        
        // Close existing WebSocket connections
        this.closeWebSocket();
        
        this.currentProvider = providerName;
        localStorage.setItem('current_data_provider', providerName);
    },

    // Get current provider
    getCurrentProvider() {
        return this.currentProvider;
    },

    // Get current provider instance
    getProvider() {
        return this.providers[this.currentProvider];
    },

    // Load symbols from current provider
    async loadSymbols() {
        const provider = this.getProvider();
        
        if (this.currentProvider === 'binance') {
            // Load both spot and futures symbols for Binance
            const spotSymbols = provider.loadSpotExchangeInfo();
            const futuresSymbols = provider.loadFuturesExchangeInfo();
            return [...spotSymbols, ...futuresSymbols];
        } else if (this.currentProvider === 'aliceblue') {
            // Load symbols from different exchanges for AliceBlue
            const nseSymbols = await provider.loadExchangeSymbols('NSE');
            const nfoSymbols = await provider.loadExchangeSymbols('NFO');
            return [...nseSymbols, ...nfoSymbols];
        } else if (this.currentProvider === 'fyers') {
            return await provider.loadSymbolMaster('NSE_CM');
        } else if (this.currentProvider === 'upstox') {
            return await provider.loadSymbolMaster('NSE_EQ');
        } else if (this.currentProvider === 'dhan') {
            return await provider.loadSymbolMaster('NSE_EQ');
        } else if (FREE_PUBLIC_PROVIDERS.includes(this.currentProvider)) {
            return await provider.loadExchangeInfo();
        }

        return [];
    },

    // Load historical data
    async loadHistoricalData(symbol, timeframe) {
        const provider = this.getProvider();

        if (this.currentProvider === 'binance') {
            return await provider.loadKlineFromBinance(symbol, timeframe);
        } else if (this.currentProvider === 'aliceblue') {
            return await provider.loadHistoricalData(symbol, timeframe);
        } else if (this.currentProvider === 'fyers') {
            return await provider.loadHistoricalData(symbol, timeframe);
        } else if (this.currentProvider === 'upstox') {
            return await provider.loadHistoricalData(symbol, timeframe);
        } else if (this.currentProvider === 'dhan') {
            return await provider.loadHistoricalData(symbol, timeframe);
        } else if (FREE_PUBLIC_PROVIDERS.includes(this.currentProvider)) {
            return await provider.loadKline(symbol, timeframe);
        }

        throw new Error('No data provider selected');
    },

    // Create WebSocket connection
    createWebSocket(onMessage, onError) {
        const provider = this.getProvider();
        
        if (this.currentProvider === 'binance') {
            // For Binance, we need to handle both spot and futures
            provider.createSpotWs(onMessage);
            provider.createFuturesWs(onMessage);
            return {
                spot: provider.binance_spot_connection,
                futures: provider.binance_futures_connection
            };
        } else if (this.currentProvider === 'aliceblue') {
            return provider.createWebSocket(onMessage, onError);
        } else if (this.currentProvider === 'fyers') {
            return provider.createWebSocket(onMessage, onError);
        } else if (this.currentProvider === 'upstox') {
            return provider.createWebSocket(onMessage, onError);
        } else if (this.currentProvider === 'dhan') {
            return provider.createWebSocket(onMessage, onError);
        } else if (FREE_PUBLIC_PROVIDERS.includes(this.currentProvider)) {
            provider.createWs();
            provider.connectWs(onMessage);
            return provider.connection;
        }

        return null;
    },

    // Subscribe to symbol data
    subscribeToSymbol(symbol, timeframe = '1m') {
        const provider = this.getProvider();
        
        if (this.currentProvider === 'binance') {
            const stream = symbol.toLowerCase() + '@kline_' + timeframe;
            if (symbol.toUpperCase().indexOf(".P") > 0) {
                provider.sendFuturesWs(`{"method": "SUBSCRIBE", "params": ["${stream}"], "id": 1}`);
            } else {
                provider.sendSpotWs(`{"method": "SUBSCRIBE", "params": ["${stream}"], "id": 1}`);
            }
        } else if (this.currentProvider === 'aliceblue') {
            const [exchange, symbolCode] = symbol.split(':');
            if (exchange && symbolCode) {
                provider.subscribeToSymbol(exchange, symbolCode);
            }
        } else if (this.currentProvider === 'fyers') {
            provider.subscribeToSymbol(symbol);
        } else if (this.currentProvider === 'upstox') {
            provider.subscribeToSymbol(symbol);
        } else if (this.currentProvider === 'dhan') {
            provider.subscribeToSymbol(symbol);
        } else if (FREE_PUBLIC_PROVIDERS.includes(this.currentProvider)) {
            provider.subscribe(symbol);
        }
    },

    // Unsubscribe from symbol data
    unsubscribeFromSymbol(symbol, timeframe = '1m') {
        const provider = this.getProvider();
        
        if (this.currentProvider === 'binance') {
            const stream = symbol.toLowerCase() + '@kline_' + timeframe;
            if (symbol.toUpperCase().indexOf(".P") > 0) {
                provider.sendFuturesWs(`{"method": "UNSUBSCRIBE", "params": ["${stream}"], "id": 1}`);
            } else {
                provider.sendSpotWs(`{"method": "UNSUBSCRIBE", "params": ["${stream}"], "id": 1}`);
            }
        } else if (this.currentProvider === 'aliceblue') {
            const [exchange, symbolCode] = symbol.split(':');
            if (exchange && symbolCode) {
                provider.unsubscribeFromSymbol(exchange, symbolCode);
            }
        } else if (this.currentProvider === 'fyers') {
            provider.unsubscribeFromSymbol(symbol);
        } else if (this.currentProvider === 'upstox') {
            provider.unsubscribeFromSymbol(symbol);
        } else if (this.currentProvider === 'dhan') {
            provider.unsubscribeFromSymbol(symbol);
        } else if (FREE_PUBLIC_PROVIDERS.includes(this.currentProvider)) {
            provider.unsubscribe(symbol);
        }
    },

    // Close WebSocket connections
    closeWebSocket() {
        const provider = this.getProvider();
        
        if (this.currentProvider === 'binance') {
            provider.terminateSpotWs();
            provider.terminateFuturesWs();
        } else if (this.currentProvider === 'aliceblue') {
            provider.closeWebSocket();
        } else if (this.currentProvider === 'fyers') {
            provider.closeWebSocket();
        } else if (this.currentProvider === 'upstox') {
            provider.closeWebSocket();
        } else if (this.currentProvider === 'dhan') {
            provider.closeWebSocket();
        } else if (FREE_PUBLIC_PROVIDERS.includes(this.currentProvider)) {
            provider.destroyWs();
        }
    },

    // Check if current provider is authenticated
    isAuthenticated() {
        if (this.currentProvider === 'aliceblue') {
            return this.providers.aliceblue.isAuthenticated();
        }
        if (this.currentProvider === 'fyers') {
            return this.providers.fyers.isAuthenticated();
        }
        if (this.currentProvider === 'upstox') {
            return this.providers.upstox.isAuthenticated();
        }
        if (this.currentProvider === 'dhan') {
            return this.providers.dhan.isAuthenticated();
        }
        // Binance and the other free public sources (Coinbase/Kraken/Bybit/
        // OKX/AMFI) don't require authentication for public data.
        return true;
    },

    // Get provider info
    getProviderInfo() {
        return {
            name: this.currentProvider,
            authenticated: this.isAuthenticated(),
            features: {
                binance: ['Crypto Spot', 'Crypto Futures', 'Real-time WebSocket'],
                aliceblue: ['Equity', 'Derivatives', 'Real-time WebSocket', 'OAuth Authentication'],
                fyers: ['NSE Equity', 'NSE Derivatives', 'BSE', 'Real-time WebSocket', 'OAuth Authentication', 'Historical Data'],
                upstox: ['NSE Equity', 'NSE Derivatives', 'BSE', 'MCX', 'Real-time WebSocket', 'OAuth Authentication', 'Historical Data'],
                dhan: ['NSE Equity', 'NSE Derivatives', 'BSE', 'MCX', 'Real-time WebSocket', 'OAuth Authentication', 'Historical Data'],
                coinbase: ['Crypto Spot', 'Real-time WebSocket'],
                kraken: ['Crypto Spot', 'Real-time WebSocket'],
                bybit: ['Crypto Spot', 'Real-time WebSocket'],
                okx: ['Crypto Spot', 'Real-time WebSocket'],
                amfi: ['Mutual Fund NAV', 'Daily History']
            }[this.currentProvider] || []
        };
    }
};
